// Cut-to-length optimiser. Pure client-side — no server call.
// Auto-loads a job's spool numbers into the input table; the user
// fills in lengths and runs the FFD bin-pack from lib/cutOptimizer.
import { useEffect, useMemo, useState } from "react";
import type { JobDto } from "../../../../../../shared/types/production-schedule";
import { binPack, type OptimizerResult } from "../lib/cutOptimizer";
import { CutOptimizerInput, type OptRow } from "./CutOptimizerInput";
import { CutOptimizerResult } from "./CutOptimizerResult";

interface Props {
  open: boolean;
  jobs: JobDto[];
  onClose(): void;
}

// Factory rather than a shared frozen object: spreading [EMPTY_ROW]
// into state would share the reference, and any later in-place edit
// (or a callback that mutated row.qty by reference) would silently
// affect every row created from the constant.
const emptyRow = (): OptRow => ({ label: "", length: "", qty: 1, note: "" });

export function CutOptimizerModal({ open, jobs, onClose }: Props) {
  const [rows, setRows] = useState<OptRow[]>(() => [emptyRow()]);
  const [stockLength, setStockLength] = useState(6000);
  const [kerf, setKerf] = useState(3);
  const [selectedJob, setSelectedJob] = useState("");
  const [result, setResult] = useState<OptimizerResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setRows([emptyRow()]);
      setResult(null);
      setError(null);
      setSelectedJob("");
    }
  }, [open]);

  const eligibleJobs = useMemo(
    () => jobs.filter((j) => j.spools.length > 0),
    [jobs],
  );

  if (!open) return null;

  const loadFromJob = (jobId: string) => {
    setSelectedJob(jobId);
    if (!jobId) return;
    const job = eligibleJobs.find((j) => String(j.id) === jobId);
    if (!job) return;
    setRows(
      job.spools.map((s) => ({
        label: s.num,
        length: "",
        qty: 1,
        note: s.description ?? "",
      })),
    );
  };

  const run = () => {
    setError(null);
    const valid = rows.filter((r) => Number(r.length) > 0 && r.qty > 0);
    if (valid.length === 0) {
      setError("Enter at least one cut with a positive length and quantity.");
      return;
    }
    const out = binPack({
      stockLength,
      kerf,
      cuts: valid.map((r) => ({
        label: r.label,
        length: Number(r.length),
        qty: r.qty,
        note: r.note,
      })),
    });
    setResult(out);
  };

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal modal-xl">
        <div className="modal-header">
          <h2>Cut-to-Length Optimiser</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 12, color: "var(--bsp-fg-muted)", marginBottom: 14, lineHeight: 1.5 }}>
            Enter available stock lengths and required cut pieces. The
            optimiser uses a First-Fit Decreasing bin-packing
            algorithm to minimise waste and show how many stock bars
            you need.
          </p>
          <div className="form-grid g3" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label>Stock Length (mm)</label>
              <input type="number" min={100} step={50} value={stockLength} onChange={(e) => setStockLength(Math.max(100, Number(e.target.value) || 6000))} />
            </div>
            <div className="form-group">
              <label>Kerf / Saw Width (mm)</label>
              <input type="number" min={0} step={0.5} value={kerf} onChange={(e) => setKerf(Math.max(0, Number(e.target.value) || 0))} />
            </div>
            <div className="form-group">
              <label>Job (auto-load spools)</label>
              <select value={selectedJob} onChange={(e) => loadFromJob(e.target.value)}>
                <option value="">— Manual entry —</option>
                {eligibleJobs.map((j) => (
                  <option key={j.id} value={j.id}>{j.jobNumber} · {j.name}</option>
                ))}
              </select>
            </div>
          </div>
          <CutOptimizerInput rows={rows} onChange={setRows} />
          {error && <div style={{ color: "var(--bsp-red-fg)", fontWeight: 700, marginBottom: 12 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <button type="button" className="btn-submit" onClick={run}>▶ Optimise</button>
            <button type="button" className="btn-cancel" onClick={() => setResult(null)}>Clear Results</button>
          </div>
          {result && <CutOptimizerResult result={result} stockLength={stockLength} />}
        </div>
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
